<template>
  <transition name="auth-fade" mode="out-in">
    <form v-if="!mfaToken" key="login" @submit.prevent="handleLogin">
      <div class="field-group">
        <label class="field-label">Username</label>
        <InputGroup>
          <InputGroupAddon>
            <i class="fas fa-user-shield"></i>
          </InputGroupAddon>
          <InputText v-model="username" type="text" placeholder="Username" required autocomplete="username" />
        </InputGroup>
      </div>

      <div class="field-group mb-4">
        <label class="field-label">Password</label>
        <Password v-model="password" :feedback="false" placeholder="Enter password" :style="{ width: '100%' }"
          :inputStyle="{ width: '100%' }" toggleMask fluid autocomplete="current-password" />
      </div>

      <button type="submit" class="btn btn-brand-yellow text-white fw-semibold w-100" :disabled="loading">
        <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        {{ loading ? 'Signing in…' : 'Login' }}
      </button>
    </form>

    <form v-else key="mfa" @submit.prevent="handleVerifyMfa">
      <div class="step-icon">
        <i class="fas fa-envelope-open-text"></i>
      </div>

      <p class="step-intro">We emailed a verification code to your account. Enter it below to finish signing in.</p>

      <div class="field-group mb-4">
        <label class="field-label">Verification code</label>
        <InputText v-model="code" class="mfa-code-input" type="text" inputmode="numeric" maxlength="6"
          placeholder="000000" required autofocus fluid autocomplete="one-time-code" />
      </div>

      <button type="submit" class="btn btn-brand-yellow text-white fw-semibold w-100" :disabled="loading">
        <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        {{ loading ? 'Verifying…' : 'Verify' }}
      </button>

      <div class="d-flex justify-content-between align-items-center mt-3">
        <button type="button" class="btn btn-link p-0 link-muted" :disabled="loading" @click="resetToLogin">
          <i class="fas fa-arrow-left me-1"></i>Back
        </button>
        <button type="button" class="btn btn-link p-0 link-muted" :disabled="loading" @click="handleLogin">Resend code</button>
      </div>
    </form>
  </transition>

  <div v-if="error" class="auth-alert mt-3" role="alert">
    <i class="fas fa-circle-exclamation me-2"></i>{{ error }}
  </div>
</template>

<script>
import { ref } from 'vue'
import InputGroup from 'primevue/inputgroup'
import InputGroupAddon from 'primevue/inputgroupaddon'
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import { useRouter } from 'vue-router'
import { useSessionStore } from '@/stores/session'
import http from '@/lib/http'

export default {
  name: "BuzzLoginAdmin",
  setup() {
    const username = ref('')
    const password = ref('')
    const code = ref('')
    const mfaToken = ref('')
    const error = ref('')
    const loading = ref(false)
    const router = useRouter()
    const session = useSessionStore()

    const applySessionAndRedirect = (data) => {
      session.setSession({
        token: data.token,
        id: data.id,
        businessId: data.businessId,
        role: data.role,
        hasAccount: data.hasAccount
      })

      const redirect = router.currentRoute.value.query.redirect
      if (data.role === 'superadmin') {
        router.push(redirect || '/superadmin')
      } else if (!data.hasAccount && data.role === 'admin') {
        router.push('/admin/create')
      } else if (data.role === 'admin' && data.hasAccount) {
        router.push(redirect || '/admin/dashboard')
      } else {
        error.value = 'Unauthorized role'
      }
    }

    const resetToLogin = () => {
      mfaToken.value = ''
      code.value = ''
      error.value = ''
    }

    const handleLogin = async () => {
      loading.value = true
      error.value = ''

      try {
        const { data } = await http.post('/auth/login', {
          username: username.value,
          password: password.value
        })

        if (data.requiresMfa) {
          mfaToken.value = data.mfaToken
          return
        }

        applySessionAndRedirect(data)
      } catch (err) {
        error.value = err?.response
          ? (err.response.data?.message || 'Login failed')
          : 'Could not reach the server. Please check your connection and try again.'
      } finally {
        loading.value = false
      }
    }

    const handleVerifyMfa = async () => {
      loading.value = true
      error.value = ''

      try {
        const { data } = await http.post('/auth/verify-mfa', {
          mfaToken: mfaToken.value,
          code: code.value
        })

        applySessionAndRedirect(data)
      } catch (err) {
        error.value = err?.response
          ? (err.response.data?.message || 'Invalid or expired code')
          : 'Could not reach the server. Please check your connection and try again.'
      } finally {
        loading.value = false
      }
    }

    return {
      username,
      password,
      code,
      mfaToken,
      error,
      loading,
      handleLogin,
      handleVerifyMfa,
      resetToLogin
    }
  },

  components: {
    InputGroup,
    InputGroupAddon,
    InputText,
    Password
  }
}
</script>

<style scoped>
.btn-brand-yellow {
  background-color: #e5c301;
  border: none;
  border-radius: 0.6rem;
  padding: 0.65rem 1rem;
  box-shadow: 0 2px 10px rgba(229, 195, 1, 0.25);
  transition: background-color 0.2s ease, box-shadow 0.2s ease, transform 0.1s ease;
}

.btn-brand-yellow:hover:not(:disabled) {
  background-color: #d4b000;
  box-shadow: 0 4px 14px rgba(229, 195, 1, 0.35);
}

.btn-brand-yellow:active:not(:disabled) {
  transform: translateY(1px);
}

.btn-brand-yellow:disabled {
  opacity: 0.75;
  cursor: not-allowed;
}

.link-muted {
  color: #6b7280;
  font-size: 0.875rem;
  text-decoration: none;
}

.link-muted:hover:not(:disabled) {
  color: #b38f00;
  text-decoration: underline;
}

.field-group {
  margin-bottom: 1.1rem;
}

.field-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.4rem;
}

.step-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin: 0 auto 1rem;
  border-radius: 50%;
  background-color: rgba(229, 195, 1, 0.15);
  color: #b38f00;
  font-size: 1.5rem;
}

.step-intro {
  text-align: center;
  color: #374151;
  line-height: 1.5;
  margin-bottom: 1.5rem;
}

.step-intro strong {
  display: block;
  font-size: 1.05rem;
  color: #18100a;
  margin-bottom: 0.15rem;
}

.mfa-code-input {
  text-align: center;
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: 0.5rem;
}

.auth-alert {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  background-color: #fdecea;
  border: 1px solid #f5c2c0;
  color: #9c2b2b;
  border-radius: 0.6rem;
  padding: 0.6rem 0.85rem;
  font-size: 0.875rem;
  text-align: left;
}

.auth-fade-enter-active,
.auth-fade-leave-active {
  transition: opacity 0.18s ease;
}

.auth-fade-enter-from,
.auth-fade-leave-to {
  opacity: 0;
}

:deep(.p-inputtext:enabled:focus),
:deep(.p-password-input:enabled:focus) {
  border-color: #e5c301 !important;
  box-shadow: 0 0 0 1px rgba(229, 195, 1, 0.35) !important;
}
</style>
